import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, ArrowLeft, Bell, Mail, Lock } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

import { AdminProvider, useAdmin } from "./admin/AdminContext";
import { AdminOrders, AdminHistory } from "./admin/AdminOrders";
import { AdminInventory, AdminEngraving } from "./admin/AdminInventory";
import { AdminPromos } from "./admin/AdminPromos";
import { AdminCatalog } from "./admin/AdminCatalog";
import { AdminGallery } from "./admin/AdminGallery";
import { AdminEvents } from "./admin/AdminEvents";
import { AdminSettings } from "./admin/AdminSettings";
import { AdminDialogs } from "./admin/AdminDialogs";

function AdminShell() {
  const navigate = useNavigate();
  const {
    settings, setSettings, togglePickup, saveAdminEmail,
    notifications, unreadCount, showNotifications, setShowNotifications, markAllRead,
    activeOrders, formatDateTime,
    saveSheetUrl, saveServiceAccount, syncToSheets, syncing, syncMessage,
  } = useAdmin();

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <div className="bg-black text-white p-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/")} className="text-white hover:text-[#FF3366] transition-colors" data-testid="admin-back-btn">
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
          <div className="flex items-center gap-2">
            <Flame size={20} strokeWidth={2.5} className="text-[#FF3366]" />
            <h1 className="font-heading text-lg font-black uppercase tracking-wider">Admin Dashboard</h1>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 hover:bg-zinc-800 transition-colors rounded" data-testid="notifications-btn">
              <Bell size={18} strokeWidth={2.5} />
              {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-[#FF3366] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center border-2 border-white">{unreadCount}</span>}
            </button>
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] max-h-96 overflow-y-auto z-50" data-testid="notifications-panel">
                <div className="p-3 border-b-2 border-black flex items-center justify-between bg-zinc-50">
                  <span className="font-heading font-bold text-xs uppercase text-black">Notifications</span>
                  <button onClick={markAllRead} className="text-[10px] text-[#FF3366] font-heading font-bold uppercase hover:underline" data-testid="mark-all-read-btn">Mark all read</button>
                </div>
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-zinc-400 text-xs">No notifications</div>
                ) : notifications.map(n => (
                  <div key={n.id} className={`p-3 border-b border-zinc-100 text-xs ${!n.read ? "bg-[#FF3366]/5" : ""}`} data-testid={`notification-${n.id}`}>
                    <p className="font-bold text-black text-xs">{n.title}</p>
                    <p className="text-zinc-500 text-[10px] mt-1">{n.body}</p>
                    <p className="text-zinc-300 text-[10px] mt-1">{formatDateTime(n.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Quick Settings Row */}
        <div className="flex flex-wrap items-center gap-6 mb-6 brutal-card p-5">
          <div className="flex items-center gap-3" data-testid="pickup-toggle-container">
            <Switch checked={settings.pickup_on_site_enabled} onCheckedChange={togglePickup} data-testid="pickup-toggle" />
            <Label className="font-heading font-bold text-xs uppercase">Pickup On Site</Label>
            <p className="text-[10px] mt-2 opacity-70 font-mono">Emails powered by Resend. Notifications sent to this address.</p>
          </div>
          <div className="flex items-center gap-2 ml-auto" data-testid="admin-email-container">
            <Mail size={14} strokeWidth={2.5} className="text-[#FF3366]" />
            <input type="email" className="brutal-input text-xs w-56" placeholder="Admin email" value={settings.admin_email || ""} onChange={e => setSettings(p => ({ ...p, admin_email: e.target.value }))} onBlur={saveAdminEmail} data-testid="admin-email-input" />
          </div>
        </div>

        {/* Resend Email Integration */}
        <div className="border-2 border-black p-5 mb-6 bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="resend-section">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={18} strokeWidth={2.5} className="text-[#FF3366]" />
            <Label className="font-heading font-bold text-sm uppercase">Resend Email Integration</Label>
          </div>
          <div>
            <label className="font-heading font-bold text-[10px] uppercase tracking-wider block mb-1">Sender Email (From Address)</label>
            <div className="flex gap-2">
              <input type="email" className="brutal-input flex-1 text-xs" placeholder="orders@boslenh.com" value={settings.resend_sender_email || ""} onChange={e => setSettings(p => ({ ...p, resend_sender_email: e.target.value }))} data-testid="resend-sender-input" />
            </div>
          </div>
          <button onClick={async () => { try { const axios = (await import("axios")).default; await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, { resend_sender_email: settings.resend_sender_email }); } catch(e) {  } }} className="brutal-btn text-xs px-4 py-2 mt-3" data-testid="save-resend-btn">Save Email Settings</button>
          <p className="text-[10px] mt-2 text-zinc-400 font-mono">Sender must use a verified domain in Resend. Default: orders@boslenh.com</p>
        </div>

        {/* Google Sheets Integration */}
        <div className="border-2 border-black p-5 mb-8 bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="gsheets-section">
          <div className="flex items-center gap-2 mb-4">
            <Label className="font-heading font-bold text-sm uppercase">Google Sheets Integration</Label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-heading font-bold text-[10px] uppercase tracking-wider block mb-1">Google Sheet URL</label>
              <div className="flex gap-2">
                <input className="brutal-input flex-1 text-xs" placeholder="https://docs.google.com/spreadsheets/d/..." value={settings.google_sheet_url || ""} onChange={e => setSettings(p => ({ ...p, google_sheet_url: e.target.value }))} data-testid="sheet-url-input" />
                <button onClick={saveSheetUrl} className="brutal-btn text-xs px-3 py-1.5" data-testid="save-sheet-url-btn">Save</button>
              </div>
            </div>
            <div>
              <label className="font-heading font-bold text-[10px] uppercase tracking-wider block mb-1">Service Account JSON</label>
              <div className="flex gap-2">
                <textarea className="brutal-input flex-1 text-[10px] font-mono min-h-[60px] resize-none" placeholder='{"type":"service_account",...}' value={settings.google_service_account_json || ""} onChange={e => setSettings(p => ({ ...p, google_service_account_json: e.target.value }))} data-testid="service-account-input" />
                <button onClick={saveServiceAccount} className="brutal-btn text-xs px-3 py-1.5 self-start" data-testid="save-service-account-btn">Save</button>
              </div>
            </div>
          </div>
          <button onClick={syncToSheets} disabled={syncing} className={`brutal-btn brutal-btn-secondary text-xs px-4 py-2 mt-3 ${syncing ? "opacity-50" : ""}`} data-testid="sync-sheets-btn">{syncing ? "Syncing..." : "Sync Now"}</button>
          {syncMessage && <p className="text-xs mt-2 text-zinc-600 font-mono">{syncMessage}</p>}
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="bg-transparent p-0 h-auto flex flex-wrap gap-2" data-testid="admin-tabs">
            {[
              { value: "orders", label: "Orders", badge: activeOrders.length > 0 ? activeOrders.length : null },
              { value: "history", label: "History" },
              { value: "inventory", label: "Inventory" },
              { value: "engraving", label: "Engraving" },
              { value: "promos", label: "Promos" },
              { value: "catalog", label: "Catalog" },
              { value: "gallery", label: "Gallery" },
              { value: "events", label: "Events" },
              { value: "contact", label: "Settings" },
            ].map(tab => (
              <TabsTrigger key={tab.value} value={tab.value}
                className="brutal-tab data-[state=active]:bg-[#FF3366] data-[state=active]:text-white data-[state=active]:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] border-2 border-black px-4 py-2 font-heading font-bold text-xs uppercase rounded-none transition-all"
                data-testid={`tab-${tab.value}`}
              >
                {tab.label}
                {tab.badge && <Badge className="ml-2 bg-[#FFD600] text-black border-0 rounded-none font-mono text-[10px]">{tab.badge}</Badge>}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="orders"><AdminOrders /></TabsContent>
          <TabsContent value="history"><AdminHistory /></TabsContent>
          <TabsContent value="inventory"><AdminInventory /></TabsContent>
          <TabsContent value="engraving"><AdminEngraving /></TabsContent>
          <TabsContent value="promos"><AdminPromos /></TabsContent>
          <TabsContent value="catalog"><AdminCatalog /></TabsContent>
          <TabsContent value="gallery"><AdminGallery /></TabsContent>
          <TabsContent value="events"><AdminEvents /></TabsContent>
          <TabsContent value="contact"><AdminSettings /></TabsContent>
        </Tabs>
      </div>

      <AdminDialogs />
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem("admin_authed") === "true");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleLogin = () => {
    if (password === "Select56") {
      sessionStorage.setItem("admin_authed", "true");
      setAuthed(true);
      setError("");
    } else {
      setError("Incorrect password");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5] p-4">
        <div className="brutal-card p-8 max-w-sm w-full text-center">
          <Lock size={32} className="mx-auto mb-4 text-[#FF3366]" strokeWidth={2.5} />
          <h2 className="font-heading text-2xl font-black uppercase mb-2">Admin Access</h2>
          <p className="text-xs text-zinc-500 mb-6">Enter the admin password to continue</p>
          <input type="password" className="brutal-input w-full mb-4" placeholder="Password" value={password}
            onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()}
            data-testid="admin-password-input" />
          {error && <p className="text-[#FF3366] text-xs font-bold mb-4" data-testid="admin-login-error">{error}</p>}
          <button onClick={handleLogin} className="brutal-btn w-full text-sm" data-testid="admin-login-btn">Enter Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <AdminProvider>
      <AdminShell />
    </AdminProvider>
  );
}
